import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';

import { startCase } from 'lodash';

import { Container, theme, useNavbarContainerHeight, useResponsive } from '@sg/garnish';
import { Allergen, type AllergenPropertyKind } from '@sg/graphql-schema';
import {
  type DietaryPreferencesContent,
  getIconForAllergen,
  useDietaryPreferencesContent,
  useProductDynamicMicroNutritionData,
} from '@sg/shared-fe-features-menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { PartnerLineItem } from '@order/shared-line-item';

import { AppFooter } from '../../../../components';
import { type GetSharedLineItemQuery } from '../../GraphQL/ShareLineItemScreen.generated';
import { usePartnerShareLineItemContent } from '../../hooks';
import { ShareLineItemLocationAlert } from '../ShareLineItemLocationAlert';

// ─────────────────────────────────────────────────────────────────────────────

export const PartnerShareLineItemScreen = (props: Props) => {
  const {
    content,
    isAddToBagDisabled,
    isAddingToBag,
    lineItem,
    shouldShowLocationPrompt,
    shouldShowUnavailablePrompt,
    onAddToBag,
  } = props;

  const router = useRouter();
  const { formatMessage } = useIntl();
  const { width: windowWidth } = useWindowDimensions();

  const { currentBreakpoint, minWidth } = useResponsive();

  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
  const { paddingTopStyle: navbarPaddingTopStyle } = useNavbarContainerHeight();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const dietaryPreferencesContent = useContentfulContentTypeEntry<DietaryPreferencesContent>({
    contentType: 'dietaryPreferences',
  });

  const { icons } = useDietaryPreferencesContent(dietaryPreferencesContent.data?.fields);

  // ─── Shared Values ───────────────────────────────────────────────────

  const scrollOffsetSV = useSharedValue(0);

  // ─── Derived ─────────────────────────────────────────────────────────

  const calories =
    typeof content.caloriesOverride === 'number' ? content.caloriesOverride : lineItem.calories;

  const nutritionData = useProductDynamicMicroNutritionData({
    calories,
    ingredients: lineItem.ingredients,
  });

  const partnerImageUrl = content.partnerImage.fields.file?.url;
  const partnerImageLabel = content.partnerImage.fields.description ?? content.partnerName;

  const buttonMessage = getButtonMessage({
    isAddToBagDisabled,
    shouldShowLocationPrompt,
    shouldShowUnavailablePrompt,
  });
  const buttonLabel = formatMessage(buttonMessage);
  const unavailableProductMessage = isAddToBagDisabled
    ? formatMessage(messages.unavailableProductForOrderMessage)
    : formatMessage(messages.unavailableProductMessage);

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasAllergens = lineItem.product.allergenProperties.length > 0;
  const hasNutritionData = nutritionData.length > 0;
  const hasNutritionNotice = Boolean(content.caloriesOverrideText);
  const shouldShowDetails = hasAllergens || hasNutritionData || hasNutritionNotice;
  const shouldShowPrompt = shouldShowLocationPrompt || shouldShowUnavailablePrompt;
  const shouldApplyNavbarSpacing = isNavbarV2Enabled && minWidth.isSM;
  const shouldUseCompactGrid = windowWidth <= 500;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffsetSV.value = event.nativeEvent.contentOffset.y;
    },
    [scrollOffsetSV],
  );

  const handleNavigateBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();

      return;
    }

    router.dismissTo('/');
  }, [router]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const heroHeadingStyle = currentBreakpoint.isXS ? styles.heroHeadingXS : undefined;
  const scrollContentContainerStyle = shouldApplyNavbarSpacing ? navbarPaddingTopStyle : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <PartnerLineItem.Container>
      {currentBreakpoint.isXS ? (
        <PartnerLineItem.NavigationHeader
          title={content.productName}
          onClose={handleNavigateBack}
          scrollOffsetSV={scrollOffsetSV}
          closeAccessibilityLabel={formatMessage(messages.closeButtonAccessibilityLabel)}
        />
      ) : null}

      <PartnerLineItem.ScrollView
        contentContainerStyle={scrollContentContainerStyle}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {shouldShowPrompt ? (
          <Container size="medium" style={styles.alertContainer}>
            {shouldShowLocationPrompt ? (
              <ShareLineItemLocationAlert message={formatMessage(messages.selectLocationMessage)} />
            ) : null}

            {shouldShowUnavailablePrompt ? (
              <ShareLineItemLocationAlert message={unavailableProductMessage} palette="caution" />
            ) : null}
          </Container>
        ) : null}

        <PartnerLineItem.Body>
          <PartnerLineItem.Hero.Container>
            <PartnerLineItem.Hero.Content>
              <PartnerLineItem.Hero.Heading style={heroHeadingStyle}>
                <PartnerLineItem.Hero.Title>{content.productName}</PartnerLineItem.Hero.Title>

                <PartnerLineItem.Hero.Byline>
                  <FormattedMessage
                    {...messages.partnerByline}
                    values={{ name: content.partnerName }}
                  />
                </PartnerLineItem.Hero.Byline>
              </PartnerLineItem.Hero.Heading>

              {!minWidth.isSM && partnerImageUrl ? (
                <PartnerLineItem.Hero.Image>
                  <PartnerLineItem.Hero.PartnerImage
                    alt={partnerImageLabel}
                    url={partnerImageUrl}
                  />

                  <PartnerLineItem.Hero.ProductImage
                    alt={lineItem.product.name}
                    url={lineItem.product.asset.url}
                  />
                </PartnerLineItem.Hero.Image>
              ) : null}

              <PartnerLineItem.Hero.Details>
                <PartnerLineItem.Hero.Description>
                  {content.description}
                </PartnerLineItem.Hero.Description>

                <PartnerLineItem.Hero.ProductName>
                  <FormattedMessage
                    {...messages.customProductName}
                    values={{ name: lineItem.product.name }}
                  />
                </PartnerLineItem.Hero.ProductName>
              </PartnerLineItem.Hero.Details>

              {minWidth.isSM ? (
                <PartnerLineItem.Hero.Button
                  accessibilityLabel={buttonLabel}
                  disabled={isAddToBagDisabled}
                  isLoading={isAddingToBag}
                  onPress={onAddToBag}
                >
                  {buttonLabel}
                </PartnerLineItem.Hero.Button>
              ) : null}
            </PartnerLineItem.Hero.Content>

            {minWidth.isSM && partnerImageUrl ? (
              <PartnerLineItem.Hero.Image>
                <PartnerLineItem.Hero.PartnerImage alt={partnerImageLabel} url={partnerImageUrl} />

                <PartnerLineItem.Hero.ProductImage
                  alt={lineItem.product.name}
                  url={lineItem.product.asset.url}
                />
              </PartnerLineItem.Hero.Image>
            ) : null}
          </PartnerLineItem.Hero.Container>

          {lineItem.ingredients.length > 0 ? (
            <PartnerLineItem.Ingredients
              shouldUseCompactGrid={shouldUseCompactGrid}
              title={formatMessage(messages.ingredientsTitle)}
            >
              {lineItem.ingredients.map((ingredient) => (
                <PartnerLineItem.Ingredient
                  key={ingredient.id}
                  imageAlt={ingredient.name}
                  imageUrl={ingredient.asset.url}
                  name={ingredient.name}
                  shouldUseCompactGrid={shouldUseCompactGrid}
                />
              ))}
            </PartnerLineItem.Ingredients>
          ) : null}

          {shouldShowDetails ? (
            <PartnerLineItem.Details>
              {hasAllergens ? (
                <PartnerLineItem.Allergens title={formatMessage(messages.allergensTitle)}>
                  {lineItem.product.allergenProperties.map((allergen) => {
                    const allergenName = formatAllergenName(allergen.name);
                    const iconUrl = getIconForAllergen(getAllergen(allergen.name), icons);

                    return (
                      <PartnerLineItem.Allergen
                        key={allergen.id}
                        iconAlt={allergenName}
                        iconUrl={iconUrl}
                        name={allergenName}
                      />
                    );
                  })}
                </PartnerLineItem.Allergens>
              ) : null}

              {hasNutritionData ? (
                <PartnerLineItem.Nutrition>
                  {nutritionData.map((nutrition) => (
                    <PartnerLineItem.NutritionItem
                      key={nutrition.label}
                      label={nutrition.label}
                      value={nutrition.value}
                    />
                  ))}
                </PartnerLineItem.Nutrition>
              ) : null}

              {content.caloriesOverrideText ? (
                <PartnerLineItem.NutritionNotice>
                  {content.caloriesOverrideText}
                </PartnerLineItem.NutritionNotice>
              ) : null}
            </PartnerLineItem.Details>
          ) : null}
        </PartnerLineItem.Body>

        {minWidth.isSM ? <AppFooter /> : null}
      </PartnerLineItem.ScrollView>

      {currentBreakpoint.isXS ? (
        <PartnerLineItem.FloatingButton
          accessibilityLabel={buttonLabel}
          disabled={isAddToBagDisabled}
          isLoading={isAddingToBag}
          onPress={onAddToBag}
        >
          {buttonLabel}
        </PartnerLineItem.FloatingButton>
      ) : null}
    </PartnerLineItem.Container>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAllergen(allergen: AllergenPropertyKind) {
  return allergen as unknown as Allergen;
}

function formatAllergenName(allergen: AllergenPropertyKind) {
  return startCase(allergen.toLowerCase().replaceAll('_', ' '));
}

function getButtonMessage(params: ButtonMessageParams) {
  const { isAddToBagDisabled, shouldShowLocationPrompt, shouldShowUnavailablePrompt } = params;

  if (shouldShowLocationPrompt) return messages.selectLocation;

  const shouldShowChangeLocation = shouldShowUnavailablePrompt && !isAddToBagDisabled;

  if (shouldShowChangeLocation) return messages.changeLocation;

  return messages.addToBag;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  alertContainer: {
    marginBottom: theme.spacing['5'],
  },
  heroHeadingXS: {
    alignItems: 'center',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  partnerByline: {
    defaultMessage: 'By {name}',
    description: 'Partner share line item | Partner byline',
  },
  customProductName: {
    defaultMessage: 'Custom {name}',
    description: 'Partner share line item | Underlying custom product name',
  },
  addToBag: {
    defaultMessage: 'Add to bag',
    description: 'Partner share line item | Add to bag CTA without price',
  },
  changeLocation: {
    defaultMessage: 'Change location',
    description: 'Partner share line item | Change location CTA',
  },
  ingredientsTitle: {
    defaultMessage: 'In This Custom',
    description: 'Partner share line item | Ingredients heading',
  },
  allergensTitle: {
    defaultMessage: 'Contains',
    description: 'Partner share line item | Allergens heading',
  },
  closeButtonAccessibilityLabel: {
    defaultMessage: 'Close',
    description: 'Partner share line item | Navigation header | Close button accessibility label',
  },
  selectLocationMessage: {
    defaultMessage: 'Select a location to order this shared item.',
    description: 'Partner share line item | Select location message',
  },
  selectLocation: {
    defaultMessage: 'Select location',
    description: 'Partner share line item | Select location CTA',
  },
  unavailableProductMessage: {
    defaultMessage: 'This item is not available at your selected location.',
    description: 'Partner share line item | Unavailable product message',
  },
  unavailableProductForOrderMessage: {
    defaultMessage: 'This item is not available for order at your selected location.',
    description: 'Partner share line item | Unavailable product for order message',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  content: NonNullable<ReturnType<typeof usePartnerShareLineItemContent>['content']>;
  isAddToBagDisabled: boolean;
  isAddingToBag: boolean;
  lineItem: SharedLineItemQueryLineItem;
  onAddToBag: () => void;
  shouldShowLocationPrompt: boolean;
  shouldShowUnavailablePrompt: boolean;
};

type ButtonMessageParams = Pick<
  Props,
  'isAddToBagDisabled' | 'shouldShowLocationPrompt' | 'shouldShowUnavailablePrompt'
>;

type SharedLineItemQueryLineItem = Extract<
  GetSharedLineItemQuery['lineItemBySlug'],
  { __typename: 'LineItemSuccess' }
>['lineItem'];
